import { callMvcApi } from "../../../../call-web-api";
import {
  ActiveUser,
  CoderGroupOption,
  GroupChoice,
  LinkedAudit,
  OutcomeOption,
  ProviderGroupOption,
  RiskAreaAuditInfoListResult,
  RiskAreaComment,
  RiskAreaDetailsDto,
  RiskAreaBulkUpdate,
  RiskAreaDetailsResult,
  RiskAreaStatus,
  RiskAreaUpdate,
} from "../types";

// Legacy MVC lookups for the per-card Set Outcome / Assign To / Comments
// controls. Same-origin MVC controllers, NOT the v1 enterprise API — the
// classic grid uses these same endpoints, so both experiences show the same
// option lists and comment threads. Comments key off riskAreaDetailId with
// EntityType "RiskAreaDetail". Relative URLs throughout, since these are
// same-domain.

const COMMENT_ENTITY_TYPE = "RiskAreaDetail";

// GET /api/RiskArea/GetRiskAreaOutcomeList → OutcomeOption[] (Set Outcome).
export const getOutcomeOptions = async (): Promise<OutcomeOption[]> => {
  const response = await callMvcApi({ url: "/api/RiskArea/GetRiskAreaOutcomeList/", method: "GET" });
  return (response as OutcomeOption[]) ?? [];
};

// GET /api/RiskArea/GetActiveUsersList → ActiveUser[] (Assign To).
export const getActiveUsers = async (): Promise<ActiveUser[]> => {
  const response = await callMvcApi({ url: "/api/RiskArea/GetActiveUsersList/", method: "GET" });
  return (response as ActiveUser[]) ?? [];
};

// Not soft-deleted (DeletedInd is a "Y"/"N" string; treat missing as active).
const notDeleted = (deletedInd: string | null | undefined): boolean =>
  (deletedInd ?? "").trim().toUpperCase() !== "Y";

const byLabel = (a: GroupChoice, b: GroupChoice): number => a.label.localeCompare(b.label);

// GET /Api/Admin/GetProviderGroupsList → ProviderGroupOption[]. Full provider
// group list for the Provider Group filter dropdown (id → value, name → label).
export const getProviderGroups = async (): Promise<GroupChoice[]> => {
  const response = await callMvcApi({ url: "/Api/Admin/GetProviderGroupsList", method: "GET" });
  return ((response as ProviderGroupOption[]) ?? [])
    .filter((g) => notDeleted(g.deletedInd) && g.name)
    .map((g) => ({ value: String(g.providerGroupId), label: g.name as string }))
    .sort(byLabel);
};

// GET /Api/Admin/GetCoderGroupsList → CoderGroupOption[]. Full coder group list
// for the Coder Group filter dropdown (refCoderGroupId → value, name → label).
export const getCoderGroups = async (): Promise<GroupChoice[]> => {
  const response = await callMvcApi({ url: "/Api/Admin/GetCoderGroupsList", method: "GET" });
  return ((response as CoderGroupOption[]) ?? [])
    .filter((g) => notDeleted(g.deletedInd) && g.coderGroupName)
    .map((g) => ({ value: String(g.refCoderGroupId), label: g.coderGroupName as string }))
    .sort(byLabel);
};

// GET /api/RiskArea/GetRiskStatusList/null → RiskAreaStatus[] (Status options).
export const getStatusOptions = async (): Promise<RiskAreaStatus[]> => {
  const response = await callMvcApi({ url: "/api/RiskArea/GetRiskStatusList/null", method: "GET" });
  return (response as RiskAreaStatus[]) ?? [];
};

// The two lookups behind the bulk update modal's Status and Outcome dropdowns —
// not the full lists above. A status/outcome applies to particular risk area
// types, so a mixed selection is only valid for options every selected type
// shares; both endpoints answer with exactly that intersection, which is what
// makes one statusId on the bulk payload correct for the whole selection.
//
// encodeURIComponent is load-bearing: the type vocabulary contains 'E&M', and an
// unescaped `&` would truncate the parameter and return the list for 'E' alone.
const combinedTypesQuery = (riskAreaTypes: string[]): string =>
  `?selectedRiskAreaCategories=${encodeURIComponent(riskAreaTypes.join(","))}`;

export const getBulkStatusOptions = async (
  riskAreaTypes: string[]
): Promise<RiskAreaStatus[]> => {
  const response = await callMvcApi({
    url: `/api/RiskArea/GetRiskStatusListWithCombinedRATypes/${combinedTypesQuery(riskAreaTypes)}`,
    method: "GET",
  });
  return (response as RiskAreaStatus[]) ?? [];
};

export const getBulkOutcomeOptions = async (
  riskAreaTypes: string[]
): Promise<OutcomeOption[]> => {
  const response = await callMvcApi({
    url: `/api/RiskArea/GetRiskAreaOutcomeListWithCombinedRATypes/${combinedTypesQuery(riskAreaTypes)}`,
    method: "GET",
  });
  return (response as OutcomeOption[]) ?? [];
};

// GET /api/audit/GetRisksLinkedAudits/{riskAreaDetailId} → the audits linked to a
// risk area. Same endpoint the classic grid's "Linked Audits" popup calls.
export const getRisksLinkedAudits = async (
  riskAreaDetailId: number
): Promise<LinkedAudit[]> => {
  const response = await callMvcApi({
    url: `/api/audit/GetRisksLinkedAudits/${riskAreaDetailId}`,
    method: "GET",
  });
  // The MVC pipeline camel-cases its responses (the classic grid binds to
  // auditTitle, auditId, …), but lower-case the first letter defensively so a
  // PascalCase payload doesn't render a grid of empty cells.
  return ((response as any[]) ?? []).map(
    (row) =>
      Object.keys(row ?? {}).reduce((acc: any, key) => {
        acc[key.charAt(0).toLowerCase() + key.slice(1)] = row[key];
        return acc;
      }, {}) as LinkedAudit
  );
};

// The E&M bell curve deep link's parameters. The v1 list rows carry
// emCategory/emcptCode but no category id or evaluation period, so these come
// off the classic payload instead.
export interface RiskAreaBellCurveParams {
  clientProviderId: number | null;
  emCategoryId: number | null;
  dateFrom: string | null;
  dateTo: string | null;
  riskAreaCohort: string | null;
}

// What Create Audit prefills New Audit Setup with. The v1 list rows carry
// neither the server-composed audit title nor the risk area's evaluation
// window, so — exactly like the bell curve parameters above — these come off the
// classic payload. Without them the title has to be assembled from row parts and
// the service dates guessed, which is what left the From Date empty on E&M and
// Metric rows.
export interface RiskAreaCreateAuditParams {
  auditTitle: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  riskArea: string | null;
  providerType: string | null;
  facilityId: number | null;
}

export interface RiskAreaAuditInfoSnapshot {
  // riskAreaDetailId → linked-audit count.
  counts: Record<number, number>;
  // riskAreaDetailId → bell curve deep-link parameters.
  bellCurve: Record<number, RiskAreaBellCurveParams>;
  // riskAreaDetailId → provider audit exclusion flag.
  providerAuditExclusion: Record<number, boolean>;
  // riskAreaDetailId → Create Audit prefill.
  createAudit: Record<number, RiskAreaCreateAuditParams>;
}

// GET /api/RiskArea/GetRiskAreaAuditInfoList/0 → the classic grid's own list
// payload, keyed by riskAreaDetailId. Read for the linked-audit count (so a
// card's button can show it before the popup opens) and the bell curve
// parameters — neither is on the v1 list rows.
//
// clientProviderId 0 = unfiltered, matching the classic default. monthValue is
// derived from the page's Lookback filter (quarters), since the classic
// endpoint only understands trailing months — a quarter is 3 months.
export const getRiskAreaAuditInfoSnapshot = async (
  isMyRiskAreas: boolean,
  quarter: number
): Promise<RiskAreaAuditInfoSnapshot> => {
  const response = await callMvcApi({
    url: `/api/RiskArea/GetRiskAreaAuditInfoList/0?isMyRiskAreas=${isMyRiskAreas}&monthValue=${quarter * 3}`,
    method: "GET",
  });
  const rows = (response as RiskAreaAuditInfoListResult)?.riskAreaAuditInfoList ?? [];
  const counts: Record<number, number> = {};
  const bellCurve: Record<number, RiskAreaBellCurveParams> = {};
  const providerAuditExclusion: Record<number, boolean> = {};
  const createAudit: Record<number, RiskAreaCreateAuditParams> = {};
  rows.forEach((row) => {
    if (!row?.riskAreaDetailId) return;
    counts[row.riskAreaDetailId] = row.linkedAuditsCount ?? 0;
    bellCurve[row.riskAreaDetailId] = {
      clientProviderId: row.clientProviderId ?? null,
      emCategoryId: row.emCategoryId ?? null,
      dateFrom: row.dateFrom ?? null,
      dateTo: row.dateTo ?? null,
      riskAreaCohort: row.riskAreaCohort ?? null,
    };
    providerAuditExclusion[row.riskAreaDetailId] = row.isProviderExcludedFromAudit ?? false;
    createAudit[row.riskAreaDetailId] = {
      auditTitle: row.auditTitle ?? null,
      dateFrom: row.dateFrom ?? null,
      dateTo: row.dateTo ?? null,
      riskArea: row.riskArea ?? null,
      providerType: row.providerType ?? null,
      facilityId: row.facilityId ?? null,
    };
  });
  return { counts, bellCurve, providerAuditExclusion, createAudit };
};

// GET /api/RiskAreaConfig/GetRiskAreaConfig → the risk area configuration. Read
// for `newProviderLockbackPeriod`, the months Create Audit looks back for a
// Provider risk area. The classic page fetches this per click rather than
// assuming a value; the server falls back to 3 when it is unset, so this does
// too instead of inventing its own default.
const NEW_PROVIDER_LOOKBACK_FALLBACK = 3;

export const getNewProviderLookbackPeriod = async (): Promise<number> => {
  try {
    const response = await callMvcApi({
      url: "/api/RiskAreaConfig/GetRiskAreaConfig",
      method: "GET",
    });
    const months = (response as any)?.newProviderLockbackPeriod;
    return typeof months === "number" && months > 0
      ? months
      : NEW_PROVIDER_LOOKBACK_FALLBACK;
  } catch (e) {
    return NEW_PROVIDER_LOOKBACK_FALLBACK;
  }
};

// GET /Api/RiskArea/GetRiskAreaDetails/{riskAreaDetailId} → the risk area's Info
// record. Same record the classic grid's Info action reads. The SP returns a
// list; only the first row is ever used.
export const getRiskAreaDetails = async (
  riskAreaDetailId: number
): Promise<RiskAreaDetailsDto | null> => {
  const response = await callMvcApi({
    url: `/Api/RiskArea/GetRiskAreaDetails/${riskAreaDetailId}`,
    method: "GET",
  });
  return ((response as RiskAreaDetailsResult)?.riskAreaDetails ?? [])[0] ?? null;
};

// GET /Api/Facility/getExcludedFacilityForMetricsByFacilityId/{facilityId} →
// truthy when the metric view is switched off for that facility. The View
// Metrics button honours the same setting the classic page's drill-down gates on.
export const isFacilityExcludedFromMetrics = async (
  facilityId: number
): Promise<boolean> => {
  const response = await callMvcApi({
    url: `/Api/Facility/getExcludedFacilityForMetricsByFacilityId/${facilityId}`,
    method: "GET",
  });
  return !!response;
};

// POST /Api/AnalyzerComments/GetCommentCategories → category names for RiskAreaDetail comments.
export const getRiskAreaCommentCategories = async (): Promise<string[]> => {
  const response = await fetch("/Api/AnalyzerComments/GetCommentCategories", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ EntityType: COMMENT_ENTITY_TYPE }),
  });
  if (!response.ok) {
    throw new Error(`Failed to get comment categories: ${response.statusText}`);
  }
  const data = await response.json();
  return (data as string[]) ?? [];
};

// POST /Api/AnalyzerComments/GetComments → the comment thread for a risk area.
export const getRiskAreaComments = async (
  riskAreaDetailId: number
): Promise<RiskAreaComment[]> => {
  const response = await fetch("/Api/AnalyzerComments/GetComments", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ EntityType: COMMENT_ENTITY_TYPE, EntityId: riskAreaDetailId }),
  });
  if (!response.ok) {
    throw new Error(`Failed to get comments: ${response.statusText}`);
  }
  const data = await response.json();
  return (data as RiskAreaComment[]) ?? [];
};

// POST /Api/AnalyzerComments/PostComment → append a comment (CreatedBy is set
// server-side from the current user). Optional: CategoryName for categorization.
export const postRiskAreaComment = async (
  riskAreaDetailId: number,
  commentText: string,
  commentType?: string | null
): Promise<void> => {
  const response = await fetch("/Api/AnalyzerComments/PostComment", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({
      EntityType: COMMENT_ENTITY_TYPE,
      EntityId: riskAreaDetailId,
      CommentText: commentText,
      CategoryName: commentType || null,
      ApplicationId: "MDAA"
    }),
  });
  if (!response.ok) {
    throw new Error(`Failed to post comment: ${response.statusText}`);
  }
};

// POST /Api/AnalyzerComments/DeleteComment → soft-delete a RiskAreaDetail comment.
export const deleteRiskAreaComment = async (commentId: number): Promise<void> => {
  const response = await fetch("/Api/AnalyzerComments/DeleteComment", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ CommentId: commentId }),
  });
  if (!response.ok) {
    throw new Error(`Failed to delete comment: ${response.statusText}`);
  }
};

// POST /Api/AnalyzerComments/UpdateCommentWithCategory → re-categorize a RiskAreaDetail comment.
export const updateRiskAreaCommentCategory = async (
  commentId: number,
  categoryName: string
): Promise<void> => {
  const response = await fetch("/Api/AnalyzerComments/UpdateCommentWithCategory", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify({ CommentId: commentId, CategoryName: categoryName }),
  });
  if (!response.ok) {
    throw new Error(`Failed to update comment category: ${response.statusText}`);
  }
};

// POST /Api/RiskArea/BulkEditRiskArea → apply one value set to a comma-joined id
// list. DELETE soft-deletes; UPDATE writes only the filled-in fields and leaves
// the rest untouched.
//
// Answers false (not an error) when the user lacks access — the boolean is the
// caller's signal that nothing changed.
const postBulkEdit = async (body: RiskAreaBulkUpdate): Promise<boolean> => {
  const response = await fetch("/Api/RiskArea/BulkEditRiskArea", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`Failed to bulk edit risk areas: ${response.statusText}`);
  }
  return (await response.json()) === true;
};

// Soft-delete a set of risk areas — the payload onConfirmBulkDeletRiskArea sends.
export const bulkDeleteRiskAreas = (riskAreaDetailIds: number[]): Promise<boolean> =>
  postBulkEdit({
    riskAreaDetailIds: riskAreaDetailIds.join(","),
    operation: "DELETE",
    status: "Deleted",
    weight: 0,
    statusId: null,
    outcome: null,
    assignedTo: null,
    assignedToName: null,
    dueDateString: null,
  });

// Apply the bulk update modal's filled-in fields to a set of risk areas.
export const bulkUpdateRiskAreas = (body: RiskAreaBulkUpdate): Promise<boolean> =>
  postBulkEdit(body);

// POST /Api/RiskArea/UpdateRiskArea → persist Status / Set Outcome / Assign To.
export const updateRiskArea = async (payload: RiskAreaUpdate): Promise<void> => {
  const response = await fetch("/Api/RiskArea/UpdateRiskArea", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "same-origin",
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`Failed to update risk area: ${response.statusText}`);
  }
};
